/**
 * Column mapping for `data_table` xlsx auto-import (hyreslista Phase 3).
 *
 * A user uploads an arbitrary spreadsheet; we map its columns onto our fixed
 * target columns, optionally with a per-column transform (e.g. a monthly
 * rent → årshyra via × 12). The mapping itself is inferred by Bedrock Nova
 * server-side (see `apps/regta-backend/.../procedures/hyreslista.ts`), but
 * the TYPES + the deterministic heuristic fallback + the apply step live
 * here so the backend and frontend agree and the logic is unit-testable.
 */

export type MappingOp = 'none' | 'multiply' | 'divide';

/** One target column ← one source header, with an optional arithmetic op. */
export type ColumnMapping = {
  target: string;
  source: string | null;
  op: MappingOp;
  factor: number | null;
  note?: string;
};

/** A target column the upload is mapped onto. */
export type MappingTargetColumn = {
  key: string;
  label: string;
  kind?: 'text' | 'number' | 'currency';
  /** Optional hint sent to Bedrock (e.g. "if monthly, × 12"). */
  description?: string;
};

export type MappingInputRow = Record<string, string | number | null>;
export type MappingOutputRow = Record<string, string | number | null>;

const norm = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim();

function toNum(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const c = v.replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, '');
    if (c === '' || c === '-') return null;
    const n = Number.parseFloat(c);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Apply a confirmed mapping to header-keyed input rows → target-keyed rows.
 * `multiply`/`divide` coerce the source to a number and round to whole units;
 * number/currency targets coerce without an op; text targets pass through.
 */
export function applyColumnMapping(
  inputRows: ReadonlyArray<MappingInputRow>,
  mappings: ReadonlyArray<ColumnMapping>,
  targetColumns: ReadonlyArray<MappingTargetColumn>,
): MappingOutputRow[] {
  const byTarget = new Map(mappings.map((m) => [m.target, m]));
  return inputRows.map((row) => {
    const out: MappingOutputRow = {};
    for (const col of targetColumns) {
      const m = byTarget.get(col.key);
      if (!m || !m.source) {
        out[col.key] = '';
        continue;
      }
      const raw = row[m.source];
      if (raw == null || raw === '') {
        out[col.key] = '';
        continue;
      }
      if (m.op === 'multiply' || m.op === 'divide') {
        const n = toNum(raw);
        const f = m.factor ?? 1;
        out[col.key] =
          n == null
            ? String(raw)
            : Math.round(m.op === 'multiply' ? n * f : f ? n / f : n);
      } else if (col.kind === 'currency' || col.kind === 'number') {
        const n = toNum(raw);
        out[col.key] = n == null ? String(raw) : n;
      } else {
        out[col.key] = String(raw);
      }
    }
    return out;
  });
}

/**
 * Deterministic baseline mapping: exact/contains label match, plus a
 * monthly→yearly heuristic for an årshyra-style target. Used as the Bedrock
 * fallback and as a sanity baseline.
 */
export function heuristicMapping(
  targetColumns: ReadonlyArray<MappingTargetColumn>,
  inputHeaders: ReadonlyArray<string>,
): ColumnMapping[] {
  const headers = inputHeaders.map((h) => ({ raw: h, n: norm(h) }));
  return targetColumns.map((col) => {
    const nk = norm(col.label);
    let source =
      headers.find((h) => h.n === nk)?.raw ??
      headers.find((h) => h.n.includes(nk) || nk.includes(h.n))?.raw ??
      null;
    let op: MappingOp = 'none';
    let factor: number | null = null;

    const isYearlyRent = col.key === 'arshyra' || /års?hyra/.test(nk);
    if (isYearlyRent) {
      const monthly = headers.find(
        (h) => /m[åa]nad|month/.test(h.n) && /hyra|rent/.test(h.n),
      );
      if (monthly && !headers.some((h) => h.n === nk)) {
        source = monthly.raw;
        op = 'multiply';
        factor = 12;
      }
    }
    return {
      target: col.key,
      source,
      op,
      factor,
      note: source ? 'Heuristisk matchning' : 'Ingen matchande kolumn',
    };
  });
}

/**
 * Sanitize raw (Bedrock) mappings into exactly one valid `ColumnMapping`
 * per target column: drops unknown targets, requires `source` to be a real
 * uploaded header, validates `op`/`factor`, and fills missing targets with a
 * null mapping. Never trusts the model's shape blindly.
 */
export function validateMappings(
  raw: unknown,
  targetColumns: ReadonlyArray<MappingTargetColumn>,
  inputHeaders: ReadonlyArray<string>,
): ColumnMapping[] {
  const list: unknown[] = Array.isArray((raw as { mappings?: unknown })?.mappings)
    ? ((raw as { mappings: unknown[] }).mappings)
    : Array.isArray(raw)
      ? raw
      : [];
  const headerSet = new Set(inputHeaders);
  const targetKeys = new Set(targetColumns.map((c) => c.key));
  const byTarget = new Map<string, ColumnMapping>();

  for (const item of list) {
    const m = item as Record<string, unknown>;
    if (!m || typeof m.target !== 'string' || !targetKeys.has(m.target)) continue;
    const source =
      typeof m.source === 'string' && headerSet.has(m.source) ? m.source : null;
    let op: MappingOp =
      m.op === 'multiply' || m.op === 'divide' ? m.op : 'none';
    let factor =
      typeof m.factor === 'number' && Number.isFinite(m.factor) ? m.factor : null;
    if (op !== 'none' && (factor == null || factor === 0)) {
      op = 'none';
      factor = null;
    }
    if (!source) {
      op = 'none';
      factor = null;
    }
    byTarget.set(m.target, {
      target: m.target,
      source,
      op,
      factor,
      note: typeof m.note === 'string' ? m.note.slice(0, 200) : undefined,
    });
  }

  return targetColumns.map(
    (c) =>
      byTarget.get(c.key) ?? {
        target: c.key,
        source: null,
        op: 'none' as const,
        factor: null,
        note: 'Ingen matchande kolumn',
      },
  );
}
